import { TYPES } from 'action-types/athena';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { generateErrors } from 'utilities/util';
import { IStore } from 'reducers/index';
import { callApiGet, callApiV2Post } from 'utilities/apiCaller';
import API from 'constants/api';

const convertErrorResponseToMessage = (errorResponse: any) => {
    if (typeof errorResponse === 'string') {
        return errorResponse;
    }

    if (Array.isArray(errorResponse.errors)) {
        const formattedMessages = errorResponse.errors.map((error: any) => {
            const key = error.key.split('.').pop();
            return `${key}: '${error.value}'`;
        });
        return formattedMessages.join(', ');
    }

    return 'Unknown error format';
};


export const getMaterialCodeAttributes = (customerBeCode: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_MATERIAL_CODE_ADDITIONAL_ATTRIBUTES.PENDING });
        const queryParams = {
            customerBeCode: `*,${customerBeCode}`,
            page: 1,
            limit: 100000,
        };

        try {
            const res = await callApiGet(API.FinanceConfig.MaterialCodeAdditionalAttributes, queryParams);
            dispatch({
                type: TYPES.FETCH_MATERIAL_CODE_ADDITIONAL_ATTRIBUTES.FULFILLED,
                payload: res.data,
            });
        } catch (e) {
            dispatch({
                type: TYPES.FETCH_MATERIAL_CODE_ADDITIONAL_ATTRIBUTES.REJECTED,
                payload: generateErrors(e),
            });
        }
    };
};


export const getChargeAmendmentExceptions = (customerBeCode: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_CHARGE_AMENDMENT_EXCEPTIONS.PENDING });
        const queryParams = { customerBeCode, page: 1, limit: 100000 };
        try {
            const res = await callApiGet(API.FinanceConfig.ChargeAmendmentExceptions, queryParams);
            dispatch({ type: TYPES.FETCH_CHARGE_AMENDMENT_EXCEPTIONS.FULFILLED, payload: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_CHARGE_AMENDMENT_EXCEPTIONS.REJECTED, payload: errors });
        }
    };
};

export const getAthenaOptionalChargeRateRetrieval = (customerBeCode: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_ATHENA_OPTIONAL_CHARGE_RATE_RETRIEVAL.PENDING });
        const queryParams = { customerBeCode, page: 1, limit: 100000 };
        try {
            const res = await callApiGet(API.FinanceConfig.AthenaOptionalChargeRateRetrieval, queryParams);
            dispatch({ type: TYPES.FETCH_ATHENA_OPTIONAL_CHARGE_RATE_RETRIEVAL.FULFILLED, payload: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_ATHENA_OPTIONAL_CHARGE_RATE_RETRIEVAL.REJECTED, payload: errors });
        }
    };
};

export const getChargeSplitRules = (contractualParty: string, shipmentType: string, chargeCode: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_CHARGE_SPLIT_RULES.PENDING });
        const limit = 1000;
        const baseParams = { contractualParty, shipmentType, ruleType: 'Optional', chargeCode: `*,${chargeCode}`, limit };
        try {
            // First page gives the total count; loop the remaining pages to gather all records
            // for this contractualParty + shipmentType + ruleType + chargeCode combination.
            const firstRes = await callApiGet(API.FinanceConfig.ChargeSplitRules, { ...baseParams, page: 1 });
            const firstData = firstRes.data || {};
            const totalCount = Number(firstData.totalCount) || 0;
            let records = firstData.data || [];
            const totalPages = Math.ceil(totalCount / limit);
            for (let page = 2; page <= totalPages; page++) {
                const res = await callApiGet(API.FinanceConfig.ChargeSplitRules, { ...baseParams, page });
                records = records.concat(res.data?.data || []);
            }
            const payload = { ...firstData, data: records, page: 1, limit };
            dispatch({ type: TYPES.FETCH_CHARGE_SPLIT_RULES.FULFILLED, payload });
            return payload;
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_CHARGE_SPLIT_RULES.REJECTED, payload: errors });
            return null;
        }
    };
};

export const getAdhocContractualRates = (documentType: string, requestObject: any) => {
    const importUrl = API.ETS.EtsAdHocContractualRates;
    const exportUrl = API.ExportJob.ExportJobsAdhocContractualRates;
    const requestUrl = documentType.toLowerCase() === 'export' ? exportUrl : importUrl;

    return (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_ADHOC_CONTRACTUAL_RATES.PENDING });

        return new Promise((resolve, reject) => {
            callApiV2Post(requestUrl, requestObject)
                .then((res) => {
                    let errorReason = res.data?.addedCharges[0]?.errorReason;
                    if (errorReason) {
                        if (errorReason.toLowerCase() === 'rates not available for requested charge/lane') {
                            errorReason = `Rates not available for requested charge/lane. Kindly click 'Amend Rates' and proceed by manually updating the details.`;
                        }
                        dispatch({
                            type: TYPES.FETCH_ADHOC_CONTRACTUAL_RATES.REJECTED,
                            payload: errorReason
                        });
                        reject(errorReason);
                    } else {
                        dispatch({
                            type: TYPES.FETCH_ADHOC_CONTRACTUAL_RATES.FULFILLED,
                            payload: res.data
                        });
                        resolve(res.data);
                    }
                })
                .catch((e) => {
                    let errorMessage = 'Error fetching Adhoc Contractual Rates';
                    if (e.response?.status !== 200 && !e.response?.data) {
                        errorMessage = 'Internal Server Error';
                    } else if (e.response?.data) {
                        errorMessage = convertErrorResponseToMessage(e.response.data);
                    } else if (e.message) {
                        errorMessage = e.message;
                    } else {
                        errorMessage = 'Error fetching Adhoc Contractual Rates';
                    }
                    dispatch({
                        type: TYPES.FETCH_ADHOC_CONTRACTUAL_RATES.REJECTED,
                        payload: errorMessage
                    });
                    reject(errorMessage);
                });
        });
    };
};
